import { create } from 'zustand';

export interface Project {
  id: string;
  name: string;
  memberIds: string[];
  createdAt: Date;
  updatedAt: Date;
}

interface ProjectsState {
  diagramId: string | null;
  projects: Project[];
  activeProjectId: string | null;
  pickModeEnabled: boolean;

  initialize: (diagramId: string) => void;
  createProject: (name: string) => Project;
  renameProject: (id: string, name: string) => void;
  deleteProject: (id: string) => void;
  addMember: (projectId: string, nodeId: string) => void;
  removeMember: (projectId: string, nodeId: string) => void;
  setActiveProject: (id: string | null) => void;
  setPickModeEnabled: (enabled: boolean) => void;
  getProject: (id: string) => Project | undefined;
}

export const useProjectsStore = create<ProjectsState>((set, get) => ({
  diagramId: null,
  projects: [],
  activeProjectId: null,
  pickModeEnabled: false,

  initialize: (diagramId) => {
    // Canonical rosters are loaded through useOrgModel. This store now owns
    // only transient canvas-pick state while legacy callers are removed.
    set({ diagramId, projects: [], activeProjectId: null, pickModeEnabled: false });
  },

  createProject: (name) => {
    const { projects } = get();
    const newProject: Project = {
      id: `project-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name,
      memberIds: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    const updatedProjects = [...projects, newProject];
    set({ projects: updatedProjects });
    return newProject;
  },

  renameProject: (id, name) => {
    const { projects } = get();
    const updatedProjects = projects.map((p) =>
      p.id === id ? { ...p, name, updatedAt: new Date() } : p
    );
    set({ projects: updatedProjects });
  },

  deleteProject: (id) => {
    const { projects, activeProjectId } = get();
    const updatedProjects = projects.filter((p) => p.id !== id);
    set({
      projects: updatedProjects,
      activeProjectId: activeProjectId === id ? null : activeProjectId,
      pickModeEnabled: activeProjectId === id ? false : get().pickModeEnabled,
    });
  },

  addMember: (projectId, nodeId) => {
    const { projects } = get();
    const updatedProjects = projects.map((p) => {
      if (p.id !== projectId) return p;
      if (p.memberIds.includes(nodeId)) return p;
      return { ...p, memberIds: [...p.memberIds, nodeId], updatedAt: new Date() };
    });
    set({ projects: updatedProjects });
  },

  removeMember: (projectId, nodeId) => {
    const { projects } = get();
    const updatedProjects = projects.map((p) => {
      if (p.id !== projectId) return p;
      return { ...p, memberIds: p.memberIds.filter((id) => id !== nodeId), updatedAt: new Date() };
    });
    set({ projects: updatedProjects });
  },

  setActiveProject: (id) => set({ activeProjectId: id }),

  setPickModeEnabled: (enabled) => set({ pickModeEnabled: enabled }),

  getProject: (id) => get().projects.find((p) => p.id === id),
}));
